import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, Star, MapPin } from "lucide-react";
import { SiteHeader } from "./SiteHeader";
import { SiteFooter } from "./SiteFooter";
import { CTABlock } from "./CTABlock";
import { HeroBackground } from "./HeroBackground";
import { browardCities, type ServiceLink } from "@/data/site";
import { getServiceImage } from "@/data/serviceImages";
import { residentialContent, commercialContent } from "@/data/serviceContent";
import residentialHero from "@/assets/heroes/residential.jpg";
import commercialHero from "@/assets/heroes/commercial.jpg";

export function HubPage({
  category,
  h1,
  intro,
  services,
  citySlug,
  cityName,
}: {
  category: "residential" | "commercial";
  h1: string;
  intro: string;
  services: ServiceLink[];
  /** When set, service cards link to /areas/$citySlug/$category/$slug instead of /$category/$slug. */
  citySlug?: string;
  cityName?: string;
}) {
  const contentMap = category === "residential" ? residentialContent : commercialContent;

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground">
          <HeroBackground
            src={category === "residential" ? residentialHero : commercialHero}
            alt={category === "residential" ? "Florida home being soft-washed at golden hour" : "Commercial storefront being pressure washed"}
          />
          <div className="relative mx-auto flex min-h-[85vh] max-w-7xl flex-col justify-center px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-28">
            <div className="max-w-3xl">
              <h1 className="text-4xl font-extrabold sm:text-5xl lg:text-6xl text-balance">{h1}</h1>
              <p className="mt-5 max-w-2xl text-lg text-primary-foreground/85">{intro}</p>
              <div className="mt-7 flex items-center gap-1 text-accent">
                {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                <span className="ml-2 text-sm">{cityName ? `${cityName}, FL • Local Broward Crew` : "Pompano Beach • Serving all of Broward"}</span>
              </div>
            </div>
          </div>
        </section>

        {/* Section heading */}
        <section className="mx-auto max-w-7xl px-4 pt-16 sm:px-6 lg:px-8">
          <div className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-accent">What We Clean</div>
          <h2 className="text-3xl font-extrabold text-primary sm:text-4xl">Our {category} services</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Every surface gets the right pressure, the right detergent, and a crew that actually shows up on time. Here's what we do across Broward County.
          </p>
        </section>

        {/* Service sections – alternating image + copy */}
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          {services.map((s, idx) => {
            const img = getServiceImage(category, s.slug);
            const content = contentMap[s.slug];
            const paragraph = content?.intro?.[0] ?? s.short;
            const highlights = content?.included?.slice(0, 4) ?? [];
            const reverse = idx % 2 === 1;

            return (
              <section
                key={s.slug}
                id={s.slug}
                className="grid items-center gap-8 border-b border-border/60 py-12 last:border-b-0 lg:grid-cols-2 lg:gap-16 lg:py-20"
              >
                {/* Image */}
                <div className={`relative ${reverse ? "lg:order-2" : ""}`}>
                  <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-muted shadow-elegant">
                    {img ? (
                      <img
                        src={img}
                        alt={`${content?.title ?? s.title} service in Broward County, Florida`}
                        loading="lazy"
                        width={1024}
                        height={768}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-primary/20 to-secondary/20" />
                    )}
                  </div>
                  {/* Decorative accent block */}
                  <div
                    className={`absolute -z-10 hidden h-full w-full rounded-2xl bg-gradient-to-br from-secondary/30 to-accent/20 lg:block ${
                      reverse ? "-left-6 top-6" : "-right-6 top-6"
                    }`}
                  />
                </div>

                {/* Copy */}
                <div className={reverse ? "md:order-1" : ""}>
                  <h3 className="text-3xl font-extrabold text-primary sm:text-4xl">
                    {content?.title ?? s.title}
                  </h3>
                  <p className="mt-4 text-base leading-relaxed text-foreground/85">{paragraph}</p>

                  {highlights.length > 0 && (
                    <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
                      {highlights.map((h, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-7 flex flex-col items-start gap-3">
                    <Link
                      to={(citySlug ? `/areas/${citySlug}/${category}/${s.slug}` : `/${category}/${s.slug}`) as string}
                      className="group inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-bold text-primary-foreground transition-all hover:bg-primary/90 hover:-translate-y-0.5"
                    >
                      Learn more about {content?.title ?? s.title}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                    <Link
                      to="/contact"
                      className="group inline-flex items-center gap-2 rounded-md bg-accent px-5 py-3 text-sm font-bold text-accent-foreground shadow-glow transition-all hover:bg-accent/90 hover:-translate-y-0.5"
                    >
                      Get a Free Quote
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        {/* Coverage area */}
        <section className="bg-muted/40">
          <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-extrabold text-primary">Coverage area</h2>
            <p className="mt-2 text-muted-foreground">Based in Pompano Beach, we cover every city in Broward County:</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {browardCities.map((c) => (
                <Link key={c} to="/areas-served" className="rounded-full border border-border bg-card px-3 py-1 text-xs hover:border-secondary hover:text-secondary">{c}</Link>
              ))}
            </div>
          </div>
        </section>

        <CTABlock />
      </main>
      <SiteFooter />
    </div>
  );
}
